// @ts-nocheck

import { eventChannel, type EventChannel } from "redux-saga";
import { take, call, fork, cancel, type Task } from "redux-saga/effects";

import { appActions } from "../containers/App/App.slice";

const INTERACTIVE_SEL =
  'button, a[href], input:not([type="hidden"]), select, textarea,' +
  '[role="button"], [role="link"], [contenteditable="true"]';

// Action types for click counter
export const CLICK_COUNTER = {
  INCREMENT: "##CLICK_COUNTER/INCREMENT",
  RESET: "##CLICK_COUNTER/RESET",
  GET: "##CLICK_COUNTER/GET"
} as const;

interface ClickCounterAction {
  type: typeof CLICK_COUNTER[keyof typeof CLICK_COUNTER];
  count?: number;
}

// Internal counter state
let counter = 0;

/**
 * Gets the current click counter value
 */
export function getClickCount(): number {
  return counter;
}

/**
 * Sets the click counter value
 */
export function setClickCount(value: number): void {
  counter = value;
}

/**
 * Resets the click counter to 0
 */
export function resetClickCount(): void {
  counter = 0;
}

// Helper functions for click detection
function isVisible(el: Element): boolean {
  const rect = (el as HTMLElement).getBoundingClientRect();
  const cs = window.getComputedStyle(el as Element);
  if (cs.display === "none" || cs.visibility === "hidden") return false;
  if (rect.width === 0 && rect.height === 0) return false;
  return true;
}

function isDisabledLike(el: Element): boolean {
  const he = el as HTMLElement;
  // native disabled / aria-disabled / inert / pointer-events none
  if ((he as HTMLButtonElement).disabled) return true;
  if (he.getAttribute("aria-disabled") === "true") return true;
  if (he.hasAttribute("inert")) return true;
  const cs = getComputedStyle(he);
  if (cs.pointerEvents === "none") return true;
  return false;
}

function isClickable(el: Element): boolean {
  const he = el as HTMLElement;

  if (he.matches(INTERACTIVE_SEL)) {
    if (he.tagName === "A") {
      const href = he.getAttribute("href") || "";
      // eslint-disable-next-line no-script-url
      if (href === "#" || href.startsWith("javascript:")) return false;
    }
    return !isDisabledLike(he) && isVisible(he);
  }

  const hasRoleBtn = he.getAttribute("role") === "button" || he.getAttribute("role") === "link";
  const tabIndex = he.hasAttribute("tabindex") ? Number(he.getAttribute("tabindex")) : NaN;
  const cursorPointer = getComputedStyle(he).cursor === "pointer";
  const focusable = !Number.isNaN(tabIndex) && tabIndex >= 0;

  // Track elements with cursor:pointer (common for React onClick handlers)
  if ((hasRoleBtn || focusable || cursorPointer) && !isDisabledLike(he) && isVisible(he)) {
    return true;
  }

  return false;
}

/**
 * Creates an event channel that listens for click events and emits
 * when a clickable element is clicked
 */
function createClickChannel(): EventChannel<ClickCounterAction> {
  return eventChannel(emitter => {
    const handler = (e: MouseEvent) => {
      let clickableEl: Element | undefined;

      if (!clickableEl && e.target instanceof Element) {
        // First check if target itself is clickable (for cursor:pointer elements)
        if (isClickable(e.target)) {
          clickableEl = e.target;
        }
        else {
          // Then check if any parent matches the interactive selector
          clickableEl = e.target.closest(INTERACTIVE_SEL) || undefined;
          if (clickableEl && !isClickable(clickableEl)) clickableEl = undefined;
        }
      }

      if (!clickableEl) return;

      // Increment the counter and emit the action
      counter++;
      emitter({
        type: CLICK_COUNTER.INCREMENT,
        count: counter
      });
    };

    document.addEventListener("click", handler, { capture: true });

    // Return unsubscribe function
    return () => {
      document.removeEventListener("click", handler, { capture: true });
    };
  });
}

/**
 * Watcher saga that manages the click channel lifecycle
 * Listens for login/logout to start/stop tracking
 */
function* watchClickChannel() {
  const channel: EventChannel<ClickCounterAction> = yield call(createClickChannel);

  try {
    while (true) {
      // Take events from the channel (this keeps the channel active)
      yield take(channel);
      // The counter is already incremented in the handler
      // We can add additional logic here if needed (e.g., dispatching Redux actions)
    }
  }
  finally {
    // Cleanup when the saga is cancelled
    channel.close();
  }
}

/**
 * Main saga watcher that manages click counter lifecycle based on auth state
 */
export default function* clickCounterSaga() {
  while (true) {
    // Wait for user login
    yield take(appActions.authUserLogin);

    // Reset counter on login
    resetClickCount();

    // Start the click channel watcher
    const clickChannelTask: Task = yield fork(watchClickChannel);

    // Wait for user logout
    yield take(appActions.authUserLogout);

    // Cancel the click channel watcher on logout
    yield cancel(clickChannelTask);

    // Reset counter on logout
    resetClickCount();
  }
}
